import {
  cloneElement,
  useEffect,
  useId,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { STATUS } from '../formatacao';

/* ───────────────────────────── Ícones ───────────────────────────── */

export type NomeIcone =
  | 'painel'
  | 'receitas'
  | 'templates'
  | 'disparos'
  | 'eventos'
  | 'configuracoes'
  | 'auditoria'
  | 'sair'
  | 'usuario';

const CAMINHOS: Record<NomeIcone, ReactNode> = {
  painel: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
    </>
  ),
  receitas: (
    <>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  templates: (
    <>
      <path d="M21 12a8 8 0 0 1-8 8H8l-4 2 1.2-4.2A8 8 0 1 1 21 12Z" />
      <path d="M8.5 10.5h7M8.5 14h4.5" />
    </>
  ),
  disparos: <path d="M21.5 3 2.8 10.4l6.2 2.6L21.5 3ZM9 13v6l3.2-3.1L21.5 3" />,
  eventos: (
    <>
      <ellipse cx="12" cy="5.5" rx="8" ry="3" />
      <path d="M4 5.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
      <path d="M4 11.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </>
  ),
  configuracoes: (
    <>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2.2" />
      <circle cx="8" cy="17" r="2.2" />
    </>
  ),
  auditoria: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  sair: (
    <>
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
      <path d="M10 8l-4 4 4 4M6 12h9" />
    </>
  ),
  usuario: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c1.2-3.4 4-5 7.5-5s6.3 1.6 7.5 5" />
    </>
  ),
};

export function Icone({
  nome,
  className,
}: {
  nome: NomeIcone;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {CAMINHOS[nome]}
    </svg>
  );
}

/* ─────────────────────────── Status ─────────────────────────── */

export function StatusBadge({ status, detalhe }: { status: string; detalhe?: string }) {
  const definicao = STATUS[status] ?? {
    rotulo: status,
    icone: '•',
    variante: 'neutro' as const,
  };
  return (
    <span className={`badge badge--${definicao.variante}`}>
      <span className="badge__icone" aria-hidden="true">
        {definicao.icone}
      </span>
      {definicao.rotulo}
      {detalhe ? <span className="sr-apenas"> — {detalhe}</span> : null}
    </span>
  );
}

export function Indicador({
  estado,
  children,
}: {
  estado: 'ok' | 'alerta' | 'erro' | 'neutro';
  children: ReactNode;
}) {
  return (
    <span className={`indicador indicador--${estado}`}>
      <span className="indicador__ponto" aria-hidden="true" />
      {children}
    </span>
  );
}

/* ─────────────────────────── Avisos ─────────────────────────── */

const ICONES_AVISO: Record<'ok' | 'alerta' | 'erro' | 'info' | 'neutro', string> = {
  ok: '✓',
  alerta: '⚠',
  erro: '✕',
  info: 'ℹ',
  neutro: 'ℹ',
};

export function Aviso({
  tipo = 'neutro',
  titulo,
  children,
}: {
  tipo?: 'ok' | 'alerta' | 'erro' | 'info' | 'neutro';
  titulo?: string;
  children: ReactNode;
}) {
  return (
    <div className={`aviso aviso--${tipo}`}>
      <span className="aviso__icone" aria-hidden="true">
        {ICONES_AVISO[tipo]}
      </span>
      <div className="aviso__corpo">
        {titulo ? <span className="aviso__titulo">{titulo}</span> : null}
        <span>{children}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────── Vazio ─────────────────────────── */

export function EstadoVazio({
  titulo,
  texto,
  icone = '◌',
  acao,
}: {
  titulo: string;
  texto: string;
  icone?: string;
  acao?: ReactNode;
}) {
  return (
    <div className="vazio">
      <span className="vazio__icone" aria-hidden="true">
        {icone}
      </span>
      <span className="vazio__titulo">{titulo}</span>
      <span className="vazio__texto">{texto}</span>
      {acao}
    </div>
  );
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <p className="carregando" role="status">
      {texto}
    </p>
  );
}

/* ─────────────────────────── Página ─────────────────────────── */

export function TituloPagina({
  titulo,
  descricao,
  acoes,
}: {
  titulo: string;
  descricao: string;
  acoes?: ReactNode;
}) {
  return (
    <header className="pagina__cabecalho">
      <div>
        <h1>{titulo}</h1>
        <p className="pagina__descricao">{descricao}</p>
      </div>
      {acoes ? <div className="linha">{acoes}</div> : null}
    </header>
  );
}

/* ─────────────────────────── Formulário ─────────────────────────── */

type PropsControle = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

export function Campo({
  rotulo,
  ajuda,
  erro,
  opcional,
  acao,
  children,
}: {
  rotulo: string;
  ajuda?: string;
  erro?: string;
  opcional?: boolean;
  /** Botão auxiliar dentro do controle (ex.: mostrar senha), fora da árvore do input. */
  acao?: ReactNode;
  children: ReactElement<PropsControle>;
}) {
  const gerado = useId();
  const id = children.props.id ?? `campo-${gerado}`;
  const idAjuda = ajuda ? `${id}-ajuda` : undefined;
  const idErro = erro ? `${id}-erro` : undefined;
  const descrito = [idAjuda, idErro].filter(Boolean).join(' ') || undefined;

  const controle = cloneElement(children, {
    id,
    'aria-describedby': descrito,
    'aria-invalid': erro ? true : undefined,
  });

  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={id}>
        {rotulo}
        {opcional ? <span className="campo__opcional"> (opcional)</span> : null}
      </label>
      {acao ? (
        <div className="campo__com-acao">
          {controle}
          <span className="campo__acao">{acao}</span>
        </div>
      ) : (
        controle
      )}
      {ajuda ? (
        <span className="campo__ajuda" id={idAjuda}>
          {ajuda}
        </span>
      ) : null}
      {erro ? (
        <span className="campo__erro" id={idErro}>
          {erro}
        </span>
      ) : null}
    </div>
  );
}

export function Switch({
  rotulo,
  checked,
  onChange,
  disabled,
}: {
  rotulo: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(evento) => onChange(evento.target.checked)}
        disabled={disabled}
      />
      <span className="switch__trilho" aria-hidden="true">
        <span className="switch__bolinha" />
      </span>
      <span>{rotulo}</span>
    </label>
  );
}

/* ─────────────────────────── Modal ─────────────────────────── */

export function Modal({
  aberto,
  titulo,
  descricao,
  onFechar,
  children,
  rodape,
}: {
  aberto: boolean;
  titulo: string;
  descricao?: string;
  onFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  const referencia = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();

  useEffect(() => {
    const dialogo = referencia.current;
    if (!dialogo) return;
    if (aberto && !dialogo.open) dialogo.showModal();
    if (!aberto && dialogo.open) dialogo.close();
  }, [aberto]);

  return (
    <dialog
      ref={referencia}
      className="modal"
      aria-labelledby={idTitulo}
      onCancel={(evento) => {
        evento.preventDefault();
        onFechar();
      }}
      onClick={(evento) => {
        if (evento.target === referencia.current) onFechar();
      }}
    >
      <div className="modal__cabecalho">
        <div className="cartao__titulo">
          <h2 id={idTitulo}>{titulo}</h2>
          {descricao ? <p className="cartao__descricao">{descricao}</p> : null}
        </div>
        <button
          type="button"
          className="botao botao--fantasma botao--pequeno"
          onClick={onFechar}
          aria-label="Fechar janela"
        >
          ✕
        </button>
      </div>
      <div className="modal__corpo">{children}</div>
      {rodape ? <div className="modal__rodape">{rodape}</div> : null}
    </dialog>
  );
}
